import React, { createContext, useState, useEffect, useCallback } from 'react';
import api from '../api/api';

export const ThemeContext = createContext();

export const ThemeProvider = ({ children }) => {
    // Initialize from localStorage or default to false (light mode)
    const [isDarkMode, setIsDarkMode] = useState(() => {
        const saved = localStorage.getItem('darkMode');
        return saved ? JSON.parse(saved) : false;
    });

    // Apply dark mode class to document root
    useEffect(() => {
        if (isDarkMode) {
            document.documentElement.classList.add('dark');
        } else {
            document.documentElement.classList.remove('dark');
        }
        localStorage.setItem('darkMode', JSON.stringify(isDarkMode));
    }, [isDarkMode]);

    // Fetch dark mode preference from backend on mount. ThemeProvider sits
    // above AuthProvider in index.js so it can't read AuthContext's user —
    // skip the call outright when there's no cached session, since it would
    // just be a guaranteed 401 duplicating AuthContext's own /user/me call.
    useEffect(() => {
        const fetchThemePreference = async () => {
            try {
                const response = await api.get('/user/me');
                if (response.data?.dark_mode !== undefined) {
                    setIsDarkMode(response.data.dark_mode);
                }
            } catch (err) {
                // If not logged in or error, use localStorage value
                console.log('Could not fetch theme preference, using local storage');
            }
        };

        if (localStorage.getItem('user')) {
            fetchThemePreference();
            return;
        }

        // No cached user yet, but a valid session cookie may still exist with
        // localStorage cleared (e.g. site data partially cleared) — retry once
        // AuthContext confirms a session instead of never checking again.
        window.addEventListener('auth:confirmed', fetchThemePreference, { once: true });
        return () => window.removeEventListener('auth:confirmed', fetchThemePreference);
    }, []);

    // Save dark mode preference to backend
    const saveThemePreference = useCallback(async (darkMode) => {
        try {
            await api.patch('/user/update_user', {
                dark_mode: darkMode
            });
        } catch (err) {
            console.error('Failed to save theme preference:', err);
        }
    }, []);

    // Toggle dark mode and save to backend
    const toggleDarkMode = useCallback(async () => {
        const newMode = !isDarkMode;
        setIsDarkMode(newMode);
        await saveThemePreference(newMode);
    }, [isDarkMode, saveThemePreference]);

    // Set dark mode and save to backend
    const setDarkMode = useCallback(async (darkMode) => {
        setIsDarkMode(darkMode);
        await saveThemePreference(darkMode);
    }, [saveThemePreference]);

    return (
        <ThemeContext.Provider value={{ isDarkMode, toggleDarkMode, setDarkMode }}>
            {children}
        </ThemeContext.Provider>
    );
};

